import { CONNECTOR_CHANNEL_PREFIX, CONNECTOR_HYDRO_KEYS, type ConnectorConfig, type ConnectorLogEntry } from '@atnt/shared';
import { hostOf } from '../util';
import { hash32, logEntry, rng, stableId, type ConnectorRecord, type OrgStore, type SensorRecord, type TenantStore } from './store';

/** A physical indicator of the tenant (from the real devices API) the seed may bind source sensors to. */
export interface IndicatorLite {
  id: string;
  deviceId: string;
  deviceName: string;
  name: string;
  unit: string | null;
  externalChannelId: string | null;
}

const HOUR = 3_600_000;
const iso = (ms: number) => new Date(ms).toISOString();
const topOfHour = (ms: number) => Math.floor(ms / HOUR) * HOUR;

export const UNIFIED_BASE_URL = 'https://monitoring.uzgidro.uz/api';

/* ───────────── organization / tenant seeds ───────────── */

/** "O'zbekgidroenergo yagona serveri" — one organization connector to the ATNT unified server, used by every reservoir. */
export function seedOrg(organizationId: string, createdBy: ConnectorRecord['createdBy']): OrgStore {
  const now = Date.now();
  const random = rng(hash32(organizationId));
  const secret = Array.from({ length: 24 }, () => 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'[Math.floor(random() * 55)]).join('');
  return {
    v: 1,
    connectors: [
      {
        id: stableId(`${organizationId}:atnt-unified`),
        name: "O'zbekgidroenergo yagona serveri",
        description: "6 ta suv omboridagi agentlar yuborgan CSV fayllardan datchiklar va 4 ta gidro ko'rsatkich (LEVEL, VOLUME, INFLOW, DISCHARGE).",
        type: 'atnt_unified',
        scope: 'organization',
        organizationId,
        tenantId: null,
        enabled: true,
        status: 'connected',
        lastSyncAt: iso(now - 4 * 60_000),
        lastError: null,
        config: { type: 'atnt_unified', baseUrl: UNIFIED_BASE_URL, apiKey: `atnt.k3Jd8Qm2.${secret}`, siteCode: null, pollIntervalSec: 300 },
        createdBy,
        createdAt: iso(now - 40 * 24 * HOUR),
        updatedAt: iso(now - 6 * 24 * HOUR),
      },
    ],
  };
}

/** One disabled example REST connector per tenant (how another organization would plug its own backend in). */
export function seedTenant(tenantId: string, organizationId: string, createdBy: ConnectorRecord['createdBy']): TenantStore {
  const now = Date.now();
  const id = stableId(`${tenantId}:rest-example`);
  const config: ConnectorConfig = {
    type: 'rest',
    baseUrl: 'https://api.example-telemetry.uz/v1',
    auth: { kind: 'bearer', token: 'demo-token-7Qk2xP9w' },
    sensorsPath: '/sensors',
    valuesPath: '/readings/latest',
    idPath: '$.id',
    valuePath: '$.value',
    timePath: '$.measuredAt',
    pollIntervalSec: 60,
  };
  return {
    v: 1,
    connectors: [
      {
        id,
        name: 'Pudratchi telemetriya API (namuna)',
        description: "Boshqa tashkilotning o'z backendini REST orqali ulash namunasi. Sinov uchun o'chirib qo'yilgan.",
        type: 'rest',
        scope: 'tenant',
        organizationId,
        tenantId,
        enabled: false,
        status: 'disabled',
        lastSyncAt: iso(now - 3 * 24 * HOUR),
        lastError: 'HTTP 401 Unauthorized: token rad etildi',
        config,
        createdBy,
        createdAt: iso(now - 12 * 24 * HOUR),
        updatedAt: iso(now - 3 * 24 * HOUR),
      },
    ],
    sensors: {},
    logs: {
      [id]: [
        logEntry('info', 'connector.disabled', 'Connector disabled', undefined, new Date(now - 3 * 24 * HOUR + 60_000)),
        logEntry('error', 'auth.failed', 'HTTP 401 Unauthorized', { status: 401 }, new Date(now - 3 * 24 * HOUR)),
        logEntry('error', 'auth.failed', 'HTTP 401 Unauthorized', { status: 401 }, new Date(now - 3 * 24 * HOUR - 60_000)),
        logEntry('info', 'sync.ok', 'Sync completed', { sensors: 8, ms: 512 }, new Date(now - 4 * 24 * HOUR)),
        logEntry('info', 'connector.created', 'Connector created', undefined, new Date(now - 12 * 24 * HOUR)),
      ],
    },
  };
}

/* ───────────── source sensors ───────────── */

type Hint = 'temp' | 'pressure' | 'level' | 'tilt' | null;
interface ColumnSpec {
  column: string;
  channel?: string | null;
  typeLabel: string | null;
  unit: string | null;
  range: [number, number] | null;
  hint?: Hint;
  /** hours since the last value */
  age?: number;
}

/** Gateway node ids per site (as in the unified server's CSV fixtures, e.g. `hisorak__65146-readings-…csv`). */
const SITE_GATEWAYS: Record<string, number[]> = {
  andijon: [65073, 65081],
  chorvoq: [134304],
  chotqol: [109227],
  hisorak: [65146, 90411],
  ohangaron: [109388],
  topolang: [134323, 134331],
};
const SITE_LEVEL: Record<string, number> = { andijon: 893.4, chorvoq: 884.2, chotqol: 925.6, hisorak: 1088.7, ohangaron: 1001.2, topolang: 1010.5 };

const HYDRO_SPEC: Record<(typeof CONNECTOR_HYDRO_KEYS)[number], Omit<ColumnSpec, 'column'>> = {
  LEVEL: { typeLabel: 'Suv sathi (hisoblangan)', unit: 'm', range: null },
  VOLUME: { typeLabel: 'Suv hajmi (hisoblangan)', unit: 'mln m³', range: [180, 420] },
  INFLOW: { typeLabel: 'Kelayotgan suv (hisoblangan)', unit: 'm³/s', range: [8, 46] },
  DISCHARGE: { typeLabel: 'Chiqarilayotgan suv (hisoblangan)', unit: 'm³/s', range: [6, 40] },
};

function unifiedNodes(siteCode: string): Array<{ node: string; columns: ColumnSpec[] }> {
  const site = siteCode || 'chotqol';
  const level = SITE_LEVEL[site] ?? 900;
  const groups: Array<{ node: string; columns: ColumnSpec[] }> = [
    {
      node: 'hydro-indicators',
      columns: CONNECTOR_HYDRO_KEYS.map((key) => ({
        column: key,
        channel: `${CONNECTOR_CHANNEL_PREFIX}${key}`,
        ...HYDRO_SPEC[key],
        range: key === 'LEVEL' ? [level - 1.5, level + 1.5] : HYDRO_SPEC[key].range,
        age: 0.6,
      })),
    },
  ];
  const gateways = SITE_GATEWAYS[site] ?? [hash32(site) % 90_000 + 10_000];
  gateways.forEach((n, i) => {
    groups.push({
      node: `${i === 0 ? 'greben' : 'tunnel'}_gateway/anl4-${n}`,
      columns: [
        ...[1, 2, 3, 4].map((ch) => ({ column: `value-${n}-Ch${ch}`, channel: `Ch${ch}`, typeLabel: 'Current loop [mA]', unit: 'mA', range: [4, 20] as [number, number], age: 0.4 })),
        { column: `eng-${n}-Ch1`, channel: 'Ch1', typeLabel: 'Engineering value', unit: null, range: [0, 100], age: 0.4 },
        { column: `eng-${n}-Ch2`, channel: 'Ch2', typeLabel: 'Engineering value', unit: null, range: null, age: 0.4 },
      ],
    });
  });
  if (site === 'chotqol' || site === 'hisorak') {
    groups.push({
      node: 'mLog',
      columns: [
        { column: 'Battery[V]', typeLabel: 'mLog', unit: 'V', range: [12.4, 13.2], age: 9 },
        { column: 'Temperature', typeLabel: 'mLog', unit: '°C', range: [18, 41], hint: 'temp', age: 9 },
        { column: 'RWL', typeLabel: 'mLog', unit: 'm', range: [12, 16], hint: 'level', age: 9 },
        { column: 'RWR', typeLabel: 'mLog', unit: 'm', range: [12, 16], age: 9 },
        { column: 'WTP02', typeLabel: 'mLog', unit: '°C', range: [11, 16], hint: 'temp', age: 9 },
        { column: 'WTP03', typeLabel: 'mLog', unit: '°C', range: [11, 16], hint: 'temp', age: 9 },
        { column: 'WTY03', typeLabel: 'mLog', unit: '°C', range: [11, 16], age: 9 },
      ],
    });
  }
  groups.push({
    node: 'Logger1',
    columns: [
      { column: 'BattV_Min', typeLabel: 'TOA5 (CR6)', unit: 'V', range: [12.6, 13.4], age: 0.05 },
      { column: 'PTemp_C', typeLabel: 'TOA5 (CR6)', unit: '°C', range: [9, 24], hint: 'temp', age: 0.05 },
      { column: 'Pz1_kPa', typeLabel: 'TOA5 (CR6)', unit: 'kPa', range: [80, 260], hint: 'pressure', age: 0.05 },
      { column: 'Pz2_kPa', typeLabel: 'TOA5 (CR6)', unit: 'kPa', range: [80, 260], hint: 'pressure', age: 0.05 },
      { column: 'Pz3_kPa', typeLabel: 'TOA5 (CR6)', unit: 'kPa', range: [80, 260], hint: 'pressure', age: 0.05 },
      { column: 'TiltX_mm_m', typeLabel: 'TOA5 (CR6)', unit: 'mm/m', range: [-0.4, 0.4], hint: 'tilt', age: 0.05 },
      { column: 'TiltY_mm_m', typeLabel: 'TOA5 (CR6)', unit: 'mm/m', range: [-0.4, 0.4], hint: 'tilt', age: 0.05 },
    ],
  });
  return groups;
}

/** Source sensors a connector would discover from its configuration. */
function nodesOf(config: ConnectorConfig, siteCode: string | null): Array<{ node: string; columns: ColumnSpec[] }> {
  switch (config.type) {
    case 'atnt_unified':
      return unifiedNodes(siteCode ?? config.siteCode ?? '');
    case 'rest': {
      const units: Array<[string, string, [number, number], Hint]> = [
        ['PZ-01', 'kPa', [90, 240], 'pressure'],
        ['PZ-02', 'kPa', [90, 240], 'pressure'],
        ['T-01', '°C', [8, 22], 'temp'],
        ['T-02', '°C', [8, 22], 'temp'],
        ['LVL-01', 'm', [850, 862], 'level'],
        ['INC-01', 'mm/m', [-0.5, 0.5], 'tilt'],
        ['Q-01', 'l/s', [1, 12], null],
        ['BAT', 'V', [12, 13.5], null],
      ];
      return [{ node: config.sensorsPath, columns: units.map(([column, unit, range, hint]) => ({ column, typeLabel: 'number', unit, range, hint, age: 0.2 })) }];
    }
    case 'mqtt': {
      const prefix = config.topic.replace(/\/?[#+][^/]*$/, '').replace(/[#+]/g, '*') || config.topic;
      return [
        {
          node: prefix,
          columns: ['level', 'pressure', 'temperature', 'battery', 'rssi'].map((name, i) => ({
            column: `${prefix}/${name}`,
            typeLabel: config.payloadFormat.toUpperCase(),
            unit: ['m', 'bar', '°C', 'V', 'dBm'][i]!,
            range: ([[850, 862], [0.5, 3], [6, 20], [3.3, 4.2], [-90, -50]] as Array<[number, number]>)[i]!,
            age: 0.01,
          })),
        },
      ];
    }
    case 'opcua':
      return [{ node: hostOf(config.endpointUrl), columns: config.nodeIds.map((id) => ({ column: id, typeLabel: 'OPC UA Variable', unit: null, range: [0, 100], age: 0.01 })) }];
    case 'modbus_tcp':
      return [
        {
          node: `${config.host}:${config.port} · unit ${config.unitId}`,
          columns: config.registers.map((r) => ({
            column: r.name,
            channel: `${r.kind}:${r.address}`,
            typeLabel: r.dataType,
            unit: r.unit ?? null,
            range: r.dataType === 'bool' ? [0, 1] : [0, 1000 * r.scale],
            age: 0.003,
          })),
        },
      ];
    case 'webhook':
      return [];
    case 'file_import':
      return [
        {
          node: config.format.toUpperCase(),
          columns: config.columns.map((c) => ({ column: c.column, typeLabel: c.name ?? null, unit: c.unit ?? null, range: [0, 100], age: 26 })),
        },
      ];
  }
}

const UNIT_CLASS: Record<string, Exclude<Hint, null>> = { '°C': 'temp', bar: 'pressure', kPa: 'pressure', MPa: 'pressure', m: 'level', 'mm/m': 'tilt' };

/**
 * Build the sensors of a connector. `previous` keeps bindings and values of columns that still exist (config edits);
 * `indicators` lets a fresh seed pre-bind a few raw sensors to the tenant's real physical indicators of the same kind.
 */
export function buildSensors(
  connector: Pick<ConnectorRecord, 'id' | 'config'>,
  siteCode: string | null,
  indicators: readonly IndicatorLite[],
  previous?: readonly SensorRecord[],
): SensorRecord[] {
  const random = rng(hash32(`${connector.id}:${siteCode ?? ''}`));
  const now = Date.now();
  const prevBy = new Map((previous ?? []).map((s) => [`${s.node}\u0000${s.columnName}`, s]));
  const used = new Set((previous ?? []).flatMap((s) => (s.binding ? [s.binding.indicatorId] : [])));
  const free = indicators.filter((i) => !i.externalChannelId?.toLowerCase().startsWith(CONNECTOR_CHANNEL_PREFIX));
  const out: SensorRecord[] = [];
  for (const group of nodesOf(connector.config, siteCode)) {
    for (const c of group.columns) {
      const prev = prevBy.get(`${group.node}\u0000${c.column}`);
      if (prev) {
        out.push({ ...prev, channel: c.channel ?? prev.channel, typeLabel: c.typeLabel, unit: c.unit });
        continue;
      }
      const value = c.range ? c.range[0] + random() * (c.range[1] - c.range[0]) : null;
      const age = (c.age ?? 1) * HOUR;
      const at = age >= HOUR / 2 ? topOfHour(now - age) : now - age;
      let binding: SensorRecord['binding'] = null;
      // pre-bind only the seeded unified-server sensors (other connectors start unbound)
      if (!previous && c.hint && connector.config.type === 'atnt_unified') {
        const match = free.find((i) => !used.has(i.id) && i.unit && UNIT_CLASS[i.unit] === c.hint);
        if (match && out.filter((s) => s.binding).length < 8) {
          used.add(match.id);
          binding = { indicatorId: match.id, deviceId: match.deviceId };
        }
      }
      out.push({
        id: stableId(`${connector.id}:${siteCode ?? ''}:${group.node}:${c.column}`),
        node: group.node,
        columnName: c.column,
        channel: c.channel ?? null,
        typeLabel: c.typeLabel,
        unit: c.unit,
        lastValue: value == null ? null : Math.round(value * 1000) / 1000,
        lastValueAt: value == null ? null : iso(at),
        binding,
      });
    }
  }
  return out;
}

/** A day and a half of sync history for a freshly seeded connector. */
export function seedLog(connector: ConnectorRecord, sensors: number): ConnectorLogEntry[] {
  if (!connector.enabled) return [logEntry('info', 'connector.created', 'Connector created', undefined, new Date(connector.createdAt))];
  const random = rng(hash32(`${connector.id}:log`));
  const now = Date.now();
  const every = Math.max(connector.config.type === 'atnt_unified' ? 300 : 'pollIntervalSec' in connector.config ? connector.config.pollIntervalSec : 600, 300) * 1000;
  const out: ConnectorLogEntry[] = [];
  for (let i = 0; i < 16; i++) {
    const at = new Date(now - i * every * 6 - random() * 60_000);
    if (i === 5) out.push(logEntry('warning', 'sensors.stale', 'Stale sensors', { count: 7 }, at));
    else if (i === 11) out.push(logEntry('error', 'source.timeout', 'Request timed out after 15000 ms', { ms: 15000 }, at));
    else if (i === 12) out.push(logEntry('warning', 'source.retry', 'Retrying in 60 s', { sec: 60 }, at));
    else out.push(logEntry('info', 'sync.ok', 'Sync completed', { sensors, ms: Math.round(180 + random() * 600) }, at));
  }
  return out;
}
